import React, { memo, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';

interface SiteListForHCPReportPdfReportPDFProps {
    data: any;
    setPdfBlob: (blob: Blob) => void;
}

const SiteListForHCPReportPdf: React.FC<SiteListForHCPReportPdfReportPDFProps> = ({ data, setPdfBlob }) => {
    const todayDate = moment().format('MM/DD/YYYY');

    // Sorting data first by DIST_NAM (district) then by SiteName
    const sortData = (data: any[]) => {
        return data.sort((a, b) => {
            const districtA = a?.DIST_NAM || '';
            const districtB = b?.DIST_NAM || '';
            const siteA = a?.SiteName || '';
            const siteB = b?.SiteName || '';

            // First compare by district, if equal, compare by site name
            const districtComparison = districtA.localeCompare(districtB);
            if (districtComparison !== 0) {
                return districtComparison;
            }
            return siteA.localeCompare(siteB);
        });
    };

    const generatePDF = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4',
        });

        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        const margin = 14;

        // Title
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('Site List For HCP', margin, 20);

        let yOffset = 30;

        // Sort the data by district and site
        const sortedData = sortData(data);

        const tableHeaders = [['District', 'Program', 'Address', 'Scope Phone', 'Scope E-mail', 'Site Director']];
        const tableData = sortedData.map((data: any) => [
            data?.DIST_NAM,
            data?.SiteName,
            `${data?.ADDR2} ${data?.ADDR3}`,
            data?.PHONE,
            data?.ScopeEmail,
            `${data?.FIRSTNAME} ${data?.LASTNAME}`,
        ]);

        // Check if there's enough space on the current page
        if (yOffset + 10 > doc.internal.pageSize.height - 20) {
            doc.addPage();
            yOffset = 20; // Reset yOffset for the new page
        }

        autoTable(doc, {
            startY: yOffset,
            head: tableHeaders,
            body: tableData,
            theme: 'plain',
            styles: {
                fontSize: 8,
                fontStyle: 'bold',
                textColor: 0,
                cellPadding: 1.8,
                lineColor: 40,
                lineWidth: 0.05,  // Thinner lines between cells
                minCellHeight: 5  // Minimized cell height to reduce vertical spacing
            },
            headStyles: {
                fontStyle: 'bold',
                textColor: 0,
                fillColor: false,
            },
            columnStyles: {
                0: { cellWidth: 'auto' },
                1: { cellWidth: 50 },
                2: { cellWidth: 70},
                3: { cellWidth: 'auto' },
                4: { cellWidth: 40 },
            },
            didDrawCell: (data: any) => {
                doc.setDrawColor(0);
                doc.setLineWidth(0.05);
            },
        });

        yOffset = (doc as any).lastAutoTable.finalY + 10; // Update yOffset for the next table

        // Add page numbers and date at the bottom
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text(todayDate, pageWidth - 30, pageHeight - 10); // Date at bottom right
            doc.text(`Page ${i} of ${pageCount}`, margin, pageHeight - 10); // Page number at bottom left
        }

        const pdfBlob = doc.output('blob');
        setPdfBlob(pdfBlob);
    };

    useEffect(() => {
        if (data) {
            generatePDF();
        }
    }, [data]);

    return null;
};

export default memo(SiteListForHCPReportPdf);
